import {
  type ProjectTimesheets,
  routes,
  type Scenario,
  Scenarios,
  timesheetToInput,
  type Timesheet,
} from '@pmai/contracts';
import { useToast } from '@pmai/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PROJECT_DERIVED_VIEWS } from '../../../entities/projects/derived-views';
import type { ApiError } from '../../../shared/api/client';
import { useApiMutation } from '../../../shared/api/hooks';
import { type EditSheet, toEditSheet } from './sheet-edit';

export type Sheets = Record<Scenario, EditSheet>;
export type SaveState = { state: 'idle' | 'saving' | 'saved' } | { state: 'failed'; error: ApiError };

const AUTOSAVE_MS = 1500;
const MAX_UNDO = 50;

/** After a save: the sheet's new version and the ids of rows that were new; rows the server no longer has lose their id. */
function merge(sheet: EditSheet, saved: Timesheet, ids: ReadonlyMap<string, string | null>): EditSheet {
  const known = new Set(saved.rows.map((r) => r.id));
  return {
    ...sheet,
    version: saved.version,
    updatedAt: saved.updatedAt,
    rows: sheet.rows.map((r) =>
      r.id ? (known.has(r.id) ? r : { ...r, id: null }) : { ...r, id: ids.get(r.key) ?? null },
    ),
  };
}

/**
 * The Plan and Fact sheets being edited on the card: every change is kept locally at once and
 * saved ~1.5 s after the last one (FR-TS-013), one save per scenario at a time; Ctrl+Z outside a
 * text field undoes the last change of either sheet, up to 50 steps (FR-TS-014).
 */
export function useTimesheetEditor(projectId: string, initial: ProjectTimesheets) {
  const { t } = useTranslation();
  const notify = useToast();
  const [sheets, setSheets] = useState<Sheets>(() => ({
    plan: toEditSheet(initial.plan),
    fact: toEditSheet(initial.fact),
  }));
  const [saveState, setSaveState] = useState<Record<Scenario, SaveState>>({
    plan: { state: 'idle' },
    fact: { state: 'idle' },
  });
  const latest = useRef(sheets);
  const history = useRef<Sheets[]>([]);
  const revision = useRef({ plan: 0, fact: 0 });
  const savedRevision = useRef({ plan: 0, fact: 0 });
  const inFlight = useRef({ plan: false, fact: false });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Turnover and the overviews are computed from the sheets — they refresh after a save.
  const save = useApiMutation(routes.timesheets.save, {
    invalidates: PROJECT_DERIVED_VIEWS,
    scope: { id: `timesheets-${projectId}` },
  });

  const show = (next: Sheets) => {
    latest.current = next;
    setSheets(next);
  };

  const flush = () => {
    clearTimeout(timer.current);
    for (const scenario of Scenarios.codes) {
      if (inFlight.current[scenario] || revision.current[scenario] === savedRevision.current[scenario])
        continue;
      const sent = latest.current[scenario];
      const rev = revision.current[scenario];
      inFlight.current[scenario] = true;
      setSaveState((s) => ({ ...s, [scenario]: { state: 'saving' } }));
      save.mutate(
        { params: { id: projectId, scenario }, body: timesheetToInput(sent) },
        {
          onSuccess: (saved) => {
            savedRevision.current[scenario] = rev;
            const ids = new Map(sent.rows.map((r, i) => [r.key, saved.rows[i]?.id ?? null]));
            history.current = history.current.map((h) => ({
              ...h,
              [scenario]: merge(h[scenario], saved, ids),
            }));
            show({ ...latest.current, [scenario]: merge(latest.current[scenario], saved, ids) });
            setSaveState((s) => ({ ...s, [scenario]: { state: 'saved' } }));
          },
          onError: (error) => setSaveState((s) => ({ ...s, [scenario]: { state: 'failed', error } })),
          onSettled: () => {
            inFlight.current[scenario] = false;
            if (revision.current[scenario] !== savedRevision.current[scenario]) schedule();
          },
        },
      );
    }
  };

  const schedule = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, AUTOSAVE_MS);
  };

  /** Applies a change to one sheet (and remembers the previous state for undo). */
  const change = (scenario: Scenario, fn: (sheet: EditSheet) => EditSheet) => {
    const prev = latest.current;
    history.current = [...history.current.slice(1 - MAX_UNDO), prev];
    revision.current[scenario] += 1;
    show({ ...prev, [scenario]: fn(prev[scenario]) });
    schedule();
  };

  const undo = () => {
    const prev = history.current.pop();
    if (!prev) {
      notify(t('timesheets.nothingToUndo'));
      return;
    }
    for (const scenario of Scenarios.codes)
      if (prev[scenario] !== latest.current[scenario]) revision.current[scenario] += 1;
    show(prev);
    schedule();
  };

  // Handlers of the latest render, for the document listener and the unmount flush.
  const undoRef = useRef(undo);
  const flushRef = useRef(flush);
  useEffect(() => {
    undoRef.current = undo;
    flushRef.current = flush;
  });

  // Ctrl+Z outside a text field (FR-TS-014); pending changes are saved when the card is left.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z' || e.shiftKey) return;
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;
      e.preventDefault();
      undoRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      flushRef.current();
    };
  }, []);

  return { sheets, saveState, change, undo };
}
